import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Camera, RefreshCw, Clock3, MapPin, Check, SkipForward, Loader2, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import AuthImage from "../components/AuthImage";

const progress = (logs) => {
  if (!logs?.length) return 0;
  const done = logs.filter((l) => l.status === "Completed" || l.status === "Skipped").length;
  return Math.round((done / logs.length) * 100);
};

export default function StaffHome() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [board, setBoard] = useState(null);
  const [station, setStation] = useState("");
  const [starting, setStarting] = useState(null);
  const [busyLog, setBusyLog] = useState(null);
  const fileRef = useRef(null);
  const pendingLogRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [t, s, b] = await Promise.all([
        api.get(`/templates?role=${encodeURIComponent(user.primary_role)}`),
        api.get("/shifts?active=true"),
        api.get("/board"),
      ]);
      setTemplates(t.data);
      setShifts(s.data.filter((x) => !x.is_auto));
      setBoard(b.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load shifts"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.primary_role]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  const updatePoolLog = async (log, status, photoPath = null) => {
    setBusyLog(log.id);
    try {
      await api.patch(`/logs/${log.id}`, { status, photo_path: photoPath });
      if (status === "Completed") toast.success(`"${log.title}" done!`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update task"));
    } finally {
      setBusyLog(null);
    }
  };

  const completePoolWithPhoto = (log) => {
    pendingLogRef.current = log;
    fileRef.current?.click();
  };

  const onPoolFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const log = pendingLogRef.current;
    if (!file || !log) return;
    setBusyLog(log.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await updatePoolLog(log, "Completed", data.path);
    } catch (err) {
      toast.error(formatApiError(err, "Photo upload failed"));
      setBusyLog(null);
    }
  };

  const poolLogs = (board?.pool || [])
    .flatMap((p) => p.logs)
    .filter((l) => l.role === user.primary_role)
    .filter((l) => !station || l.station === station || !l.station);

  const startShift = async (templateId) => {
    setStarting(templateId);
    try {
      const { data } = await api.post("/shifts/start", { template_id: templateId });
      toast.success("Shift started — let's prep!");
      navigate(`/app/shift/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not start shift"));
    } finally {
      setStarting(null);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="staff-home">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">
        Ready when you are, <span className="text-terra-600">{user.name.split(" ")[0]}</span>
      </h1>
      <p className="text-sm text-slate-500 mt-1">Pick a prep shift to start your checklist.</p>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPoolFile} data-testid="pool-photo-file-input" />

      {board && (
        <section className="mt-6" data-testid="shift-windows-board">
          <div className="p-4 sm:p-5 rounded-2xl bg-white border border-[#E4DDD3]">
            <div className="flex items-center gap-2 flex-wrap text-sm text-slate-600">
              <Clock3 className="w-4 h-4 text-terra-600" />
              Business day <span className="font-mono text-terra-600" data-testid="board-biz-date">{board.biz_date}</span>
              <span className="text-slate-500">(starts {board.business_day_start})</span>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {board.windows.map((w) => (
                <span
                  key={w.id}
                  data-testid={`window-chip-${w.id}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${w.is_active ? "bg-[#FDF0EB] border-terra-400 text-terra-600" : "bg-white border-[#E4DDD3] text-slate-500"}`}
                >
                  {w.name} {w.start}–{w.end}{w.is_active ? " · ACTIVE" : ""}
                </span>
              ))}
            </div>
          </div>

          {board.stations.length > 0 && (
            <div className="flex gap-2 mt-5 flex-wrap" data-testid="station-picker">
              <StationChip label="All Stations" active={station === ""} onClick={() => setStation("")} testid="station-chip-all" />
              {board.stations.map((s) => (
                <StationChip key={s} label={s} active={station === s} onClick={() => setStation(s)} testid={`station-chip-${s.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </div>
          )}

          {poolLogs.length > 0 && (
            <div className="mt-6 space-y-3" data-testid="pool-task-list">
              <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider">Shared Tasks</h2>
              {poolLogs.map((log) => (
                <PoolTaskRow key={log.id} log={log} busy={busyLog === log.id} onStatus={updatePoolLog} onPhoto={completePoolWithPhoto} />
              ))}
            </div>
          )}
        </section>
      )}

      <section className="mt-8">
        <h2 className="text-base md:text-lg font-semibold text-slate-700 mb-4 uppercase tracking-wider">Start a Shift — {user.primary_role}</h2>
        {templates.length === 0 ? (
          <div className="p-6 rounded-2xl bg-white border border-[#E4DDD3] text-slate-500 text-sm" data-testid="no-templates-message">
            No shift templates for your role yet. Your manager can create them under Templates.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="p-5 rounded-2xl bg-white border border-[#E4DDD3] task-card" data-testid={`template-card-${t.id}`}>
                <div className="font-display font-semibold text-lg text-[#1A1D20]">{t.name}</div>
                <div className="text-xs text-slate-500 mt-1">{t.tasks.filter((x) => !x.recurrence || x.recurrence === "none").length} manual tasks · {t.tasks.filter((x) => x.recurrence && x.recurrence !== "none").length} recurring · {t.tasks.filter((x) => x.requires_photo_proof).length} need photo</div>
                {t.window_name && (
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] text-slate-500" data-testid={`staff-template-window-${t.id}`}>
                    {t.window_name} window
                  </span>
                )}
                {(() => {
                  const manualCount = t.tasks.filter((x) => !x.recurrence || x.recurrence === "none").length;
                  if (t.my_today_instance_status === "active") {
                    return (
                      <button
                        onClick={() => navigate(`/app/shift/${t.my_today_instance_id}`)}
                        data-testid={`resume-shift-button-${t.id}`}
                        className="mt-4 w-full h-12 rounded-xl bg-emerald-500 text-white font-display font-bold flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors"
                      >
                        <Play className="w-4 h-4" /> Resume Shift
                      </button>
                    );
                  }
                  if (t.my_today_instance_status) {
                    return (
                      <button
                        disabled
                        data-testid={`start-shift-button-${t.id}`}
                        className="mt-4 w-full h-12 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-slate-500 font-display font-bold flex items-center justify-center gap-2"
                      >
                        <Check className="w-4 h-4" /> Done for Today
                      </button>
                    );
                  }
                  if (t.scheduled_today === false) {
                    return (
                      <button
                        disabled
                        data-testid={`start-shift-button-${t.id}`}
                        className="mt-4 w-full h-12 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-slate-500 font-display font-bold flex items-center justify-center gap-2"
                      >
                        <CalendarDays className="w-4 h-4" /> Not Scheduled Today
                      </button>
                    );
                  }
                  if ((t.scheduled_user_ids || []).length > 0 && !t.scheduled_user_ids.includes(user.id)) {
                    return (
                      <button
                        disabled
                        data-testid={`start-shift-button-${t.id}`}
                        className="mt-4 w-full h-12 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-slate-500 font-display font-bold flex items-center justify-center gap-2"
                      >
                        <Users className="w-4 h-4" /> Scheduled: {(t.scheduled_user_names || []).map((n) => n.split(" ")[0]).join(", ")}
                      </button>
                    );
                  }
                  if (t.window_active === false) {
                    return (
                      <button
                        disabled
                        data-testid={`start-shift-button-${t.id}`}
                        className="mt-4 w-full h-12 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-slate-500 font-display font-bold flex items-center justify-center gap-2"
                      >
                        <Clock3 className="w-4 h-4" /> Opens {t.window_start}
                      </button>
                    );
                  }
                  return (
                    <button
                      onClick={() => startShift(t.id)}
                      disabled={starting === t.id || manualCount === 0}
                      data-testid={`start-shift-button-${t.id}`}
                      className="mt-4 w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold flex items-center justify-center gap-2 hover:bg-terra-600 disabled:opacity-50 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      {starting === t.id ? "Starting…" : "Start Shift"}
                    </button>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider">Live on the Line</h2>
          <button onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:text-terra-600 transition-colors" data-testid="refresh-shifts-button" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {shifts.length === 0 ? (
          <div className="p-6 rounded-2xl bg-white border border-[#E4DDD3] text-slate-500 text-sm" data-testid="no-active-shifts-message">
            No active shifts right now.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shifts.map((s) => {
              const pct = progress(s.logs);
              const mine = s.started_by === user.id;
              return (
                <button
                  key={s.id}
                  onClick={() => navigate(`/app/shift/${s.id}`)}
                  data-testid={`active-shift-card-${s.id}`}
                  className={`p-5 rounded-2xl border text-left task-card ${mine ? "bg-[#FDF0EB] border-terra-400/50" : "bg-white border-[#E4DDD3]"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display font-semibold text-[#1A1D20]">{s.template_name}</div>
                    {mine && <span className="text-xs px-2 py-0.5 rounded-full bg-terra-500 text-white font-bold">YOURS</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {s.started_by_name} · {s.role} · {formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-[#FAF6F0] overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                    {pct}% done
                    {s.logs.some((l) => l.photo_path) && <Camera className="w-3.5 h-3.5 text-terra-600" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}

function StationChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-10 px-4 rounded-full border text-sm font-medium transition-colors ${active ? "bg-terra-500 text-white border-terra-400" : "bg-white border-[#E4DDD3] text-slate-600 hover:border-terra-400/50"}`}
    >
      {label}
    </button>
  );
}

function PoolTaskRow({ log, busy, onStatus, onPhoto }) {
  return (
    <div className="task-card p-4 sm:p-5 rounded-2xl bg-white border border-[#E4DDD3]" data-testid={`pool-task-${log.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-[#1A1D20]">{log.title}</div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {log.target_par_qty && <span className="text-xs font-mono text-terra-600/80">PAR {log.target_par_qty}</span>}
            {log.station && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] text-slate-600">
                <MapPin className="w-3 h-3" />{log.station}
              </span>
            )}
            {log.requires_photo_proof && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <Camera className="w-3.5 h-3.5" /> Photo required
              </span>
            )}
          </div>
          {log.description && <p className="text-xs text-slate-500 mt-2">{log.description}</p>}
        </div>
        <StatusBadge status={log.status} />
      </div>
      {log.photo_path && (
        <div className="mt-3">
          <AuthImage path={log.photo_path} className="w-24 h-24 object-cover rounded-xl border border-[#E4DDD3]" />
        </div>
      )}
      {(log.status === "Pending" || log.status === "In Progress") && (
        busy ? (
          <div className="mt-3 h-11 grid place-items-center text-terra-600"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {log.requires_photo_proof ? (
              <button onClick={() => onPhoto(log)} data-testid={`pool-photo-complete-${log.id}`} className="h-11 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5">
                <Camera className="w-4 h-4" /> Photo & Done
              </button>
            ) : (
              <button onClick={() => onStatus(log, "Completed")} data-testid={`pool-complete-${log.id}`} className="h-11 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5">
                <Check className="w-4 h-4" /> Done
              </button>
            )}
            <button onClick={() => onStatus(log, "Skipped")} data-testid={`pool-skip-${log.id}`} className="h-11 rounded-xl border border-rose-300/40 text-rose-600 text-sm font-medium hover:bg-rose-50 transition-colors flex items-center justify-center gap-1.5">
              <SkipForward className="w-4 h-4" /> Skip
            </button>
          </div>
        )
      )}
    </div>
  );
}
